import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSiteViewer } from "../context/ViewerContext";
import { Star, Trash2, Plus, ExternalLink } from "lucide-react";

export default function BookmarksPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { open } = useSiteViewer();
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [load, setLoad] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) { navigate("/login", { state: { from: "/bookmarks" } }); return; }
    api.get("/bookmarks").then(({ data }) => setItems(data)).finally(() => setLoad(false));
  }, [user, loading, navigate]);

  const add = async (e) => {
    e.preventDefault();
    const u = url.trim();
    if (!u) return;
    const { data } = await api.post("/bookmarks", { url: u, title: title || u });
    setItems((x) => [data, ...x]);
    setUrl(""); setTitle(""); setAdding(false);
  };
  const remove = async (id) => {
    await api.delete(`/bookmarks/${id}`);
    setItems((x) => x.filter((i) => i.id !== id));
  };

  if (loading || !user || user === false) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 py-12" data-testid="bookmarks-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight flex items-center gap-3">
            <Star size={28} className="text-[var(--omni-accent)]" /> Favoris
          </h1>
          <Button onClick={() => setAdding((a) => !a)} className="bg-[var(--omni-accent)] hover:bg-[var(--omni-accent-hover)] text-white rounded-full" data-testid="add-bookmark-btn">
            <Plus size={15} className="mr-1" /> Ajouter
          </Button>
        </div>

        {adding && (
          <form onSubmit={add} className="border border-[var(--omni-border)] rounded-lg p-5 mb-6 space-y-3" data-testid="add-bookmark-form">
            <Input placeholder="https://exemple.com" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="bm-url-input" />
            <Input placeholder="Titre (optionnel)" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="bm-title-input" />
            <div className="flex gap-2">
              <Button type="submit" className="bg-black hover:bg-gray-800 text-white rounded-full" data-testid="bm-save-btn">Enregistrer</Button>
              <Button type="button" variant="ghost" onClick={() => setAdding(false)} data-testid="bm-cancel-btn">Annuler</Button>
            </div>
          </form>
        )}

        {load && <div className="text-gray-500">Chargement…</div>}
        {!load && items.length === 0 && (
          <div className="border border-dashed border-[var(--omni-border)] rounded-lg p-12 text-center text-gray-500">
            Aucun favori. Cliquez sur l'étoile depuis le visualiseur pour en ajouter.
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {items.map((b) => (
            <div key={b.id} className="border border-[var(--omni-border)] rounded-lg p-4 hover:shadow-sm transition group" data-testid={`bm-item-${b.id}`}>
              <div className="flex items-start gap-3">
                <img src={`https://www.google.com/s2/favicons?domain=${b.url}&sz=32`} alt="" className="w-6 h-6 mt-1 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <button onClick={() => open(b.url, b.title)} className="font-medium hover:underline truncate block w-full text-left" data-testid={`bm-open-${b.id}`}>
                    {b.title}
                  </button>
                  <div className="text-xs text-gray-500 truncate">{b.url}</div>
                </div>
                <button onClick={() => remove(b.id)} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 p-1" data-testid={`bm-delete-${b.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
